import React, { Component, KeyboardEvent, RefObject, createRef } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Layer, Stage, Transformer } from 'react-konva'
import { KonvaEventObject } from 'konva/lib/Node'
import {
  Transformer as HTMLTransformerElement
} from 'konva/lib/shapes/Transformer'
import { produce } from 'immer'
import { RootState } from '../../store'
import unitUtil from '../../utils/unit.util'
import ZoomProperty from './ZoomProperty'
import {
  resetSelectedElement,
  transformElement,
  dragElement,
  replaceSelectedElement,
  moveSelectedElement,
  moveElementLayerToBottom,
  moveElementLayerToTop,
  replaceElementText,
  clearSelectedElement
} from '../../views/LabelBuilder/LabelBuilder.slice'
import { ConfirmParams } from '../Confirm/Confirm.state'
import confirmService from '../../services/confirm.service'
import { i18n } from '../../i18n/i18n'
import Card from '../Card'
import Grid from './Grid'
import Elements from './Elements'
import History from './History'
import RfidBadge from './RfidBadge'
import Konva from 'konva'

// ✅ SNAPPING IMPORT
import {
  getLineGuideStops,
  getObjectSnappingEdges,
  getGuides,
  drawGuides
} from '../../utils/konvaSnap.util'

interface Props {
  transformerRef: RefObject<HTMLTransformerElement>
}

type CombinedProps = Props & ConnectedProps<typeof connector>

interface State {
  popupMenuVisible: boolean;
  menuPosition: { x: number; y: number };
  selectedShape: Konva.Rect | null;
}

class Template extends Component<CombinedProps, State> {
  layerRef = createRef<Konva.Layer>()

  menuItemStyle: React.CSSProperties = {
    padding: '10px 20px',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: 'black',
    borderBottom: '1px solid #ddd'
  }

  constructor (props: CombinedProps) {
    super(props)
    this.state = {
      popupMenuVisible: false,
      menuPosition: { x: 0, y: 0 },
      selectedShape: null
    }
    this.handleElementRightClick = this.handleElementRightClick.bind(this)
  }

  // ✅ SNAPPING DRAG MOVE
  handleLayerDragMove = (e: KonvaEventObject<DragEvent>) => {
    const layer = this.layerRef.current
    const stage = e.target.getStage()
    if (!layer || !stage) return

    layer.find('.guid-line').forEach(l => l.destroy())

    const lineGuideStops = getLineGuideStops(stage, e.target)
    const itemBounds = getObjectSnappingEdges(e.target)
    const guides = getGuides(lineGuideStops, itemBounds)

    drawGuides(layer, guides)

    const absPos = e.target.absolutePosition()
    guides.forEach((lg: any) => {
      if (lg.orientation === 'V') absPos.x = lg.lineGuide + lg.offset
      if (lg.orientation === 'H') absPos.y = lg.lineGuide + lg.offset
    })
    e.target.absolutePosition(absPos)
  }

  componentDidUpdate (prevProps: CombinedProps) {
    // A GS1 barcode changes size when its preview is redrawn; keep the
    // selection box around it.
    if (prevProps.elements !== this.props.elements) {
      this.props.transformerRef.current?.forceUpdate()
    }
  }

  handleStageClick = (event: KonvaEventObject<MouseEvent>) => {
    const stageElement = event.target
    if (!stageElement) return
    const clickedOnEmpty = stageElement === stageElement.getStage()
    if (!clickedOnEmpty) return
    this.props.resetSelectedElement()
    this.props.transformerRef.current?.nodes([])
  }

  handleElementClick = (event: KonvaEventObject<MouseEvent>) => {
    const element = event.currentTarget
    const isMulti = event.evt.shiftKey

    let targetElement = element
    let parent = element.getParent()

    while (parent && parent.getType() !== 'Stage') {
      if (parent.getType() === 'Group') {
        targetElement = parent
        break
      }
      parent = parent.getParent()
    }

    this.props.replaceSelectedElement(targetElement, isMulti)

    const transformerNode = this.props.transformerRef.current
    if (transformerNode) {
      const stage = targetElement.getStage()
      if (!stage) return

      const selectedIds = this.props.selectedElements || [targetElement.getAttr('id')]
      const nodes = selectedIds
          .map((id: string) => stage.findOne(`#${id}`))
          .filter(n => !!n) as any[]

      transformerNode.nodes(nodes)
      transformerNode.getLayer()?.batchDraw()
    }
  }

  handleElementRightClick (event: KonvaEventObject<PointerEvent>) {
    event.evt.preventDefault()
    const shape = event.target as Konva.Rect

    this.setState({
      selectedShape: shape,
      popupMenuVisible: true,
      menuPosition: {
        x: event.evt.clientX,
        y: event.evt.clientY
      }
    })
  }

  onTextUpdate = (id: string, textNode: Konva.Text, newText: string) => {
    this.props.replaceElementText(id, newText)
  }

  handleElementTransform = (event: KonvaEventObject<MouseEvent>) => {
    this.props.transformElement(event.currentTarget)
  }

  // ✅ MODIFIED ONLY TO CLEAR GUIDES
  handleElementDrag = (event: KonvaEventObject<MouseEvent>) => {
    this.layerRef.current?.find('.guid-line').forEach(l => l.destroy())
    this.props.dragElement(event.currentTarget)
  }

  /**
   * A keystroke meant for a field the user is typing in, not for the canvas.
   * Text elements edit through a textarea laid over the stage, and its
   * keystrokes bubble up to this handler - without this guard, deleting a
   * character would offer to delete the element.
   */
  isTypingTarget (target: EventTarget | null) {
    if (!(target instanceof HTMLElement)) return false
    if (target.isContentEditable) return true
    return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
  }

  /**
   * Delete removes the selected elements, behind the same confirmation the
   * Clear element button uses - the canvas has no undo for a stray keypress,
   * and a label is usually several minutes of work.
   */
  handleDeleteKey () {
    const count = (this.props.selectedElements || []).length
    if (!count) return

    const question = count > 1
      ? i18n('Are you sure to clear the selected elements?')
      : i18n('Are you sure to clear the element?')

    const confirm: ConfirmParams = {
      id: 0,
      type: 'warning',
      question,
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => {
        this.props.clearSelectedElement()
        this.props.transformerRef.current?.nodes([])
      }
    }
    confirmService.push(confirm)
  }

  handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const { selectedElements } = this.props
    if (!selectedElements) return
    if (this.isTypingTarget(event.target)) return

    const movePoint = event.shiftKey ? 10 : 1
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault()
        this.props.moveSelectedElement(0, -movePoint)
        break
      case 'ArrowDown':
        event.preventDefault()
        this.props.moveSelectedElement(0, movePoint)
        break
      case 'ArrowLeft':
        event.preventDefault()
        this.props.moveSelectedElement(-movePoint, 0)
        break
      case 'ArrowRight':
        event.preventDefault()
        this.props.moveSelectedElement(movePoint, 0)
        break
      case 'Delete':
      case 'Backspace':
        event.preventDefault()
        this.handleDeleteKey()
        break
    }
  }

  get stageStyle () {
    return {
      transform: `scale(${this.props.zoom})`,
      transformOrigin: '0 0',
      paddingBottom: '10px',
      paddingRight: '10px',
      width: 'fit-content'
    }
  }

  get stageWidth () {
    return unitUtil.unitToPixel(this.props.stage.unit, this.props.stage.width)
  }

  get stageHeight () {
    return unitUtil.unitToPixel(this.props.stage.unit, this.props.stage.height)
  }

  render () {
    const { popupMenuVisible, menuPosition } = this.state

    return (
        <Card flat className="border border-gray-200 bg-gray-200 h-full pt-0 pb-1.5" background='third'>
          <div className='py-1.5 flex gap-1.5'>
            <ZoomProperty/>
            <History/>
            <RfidBadge/>
          </div>

          <div tabIndex={1} onKeyDown={this.handleKeyDown}>
            <Stage
                width={this.stageWidth}
                height={this.stageHeight}
                style={this.stageStyle}
                onClick={this.handleStageClick}
            >
              <Grid stage={this.props.stage}/>
              <Elements
                  elements={this.props.elements}
                  onElementClick={this.handleElementClick}
                  onElementTransform={this.handleElementTransform}
                  onElementDrag={this.handleElementDrag}
                  onElementRightClick={this.handleElementRightClick}
                  onTextUpdate={this.onTextUpdate}
                  layerRef={this.layerRef}                  // ✅ NEW
                  onLayerDragMove={this.handleLayerDragMove} // ✅ NEW
                  stageWidth={this.stageWidth}   // ✅ ADD THIS LINE
              />
              <Layer id="transformer">
                <Transformer
                    ref={this.props.transformerRef}
                    resizeEnabled={!this.props.gs1Selected}
                    rotationSnaps={[0, 90, 180, 270]}
                    rotationSnapTolerance={10}
                />
              </Layer>
            </Stage>
          </div>

          {popupMenuVisible && (
              <div
                  style={{
                    position: 'absolute',
                    top: menuPosition.y,
                    left: menuPosition.x,
                    backgroundColor: 'white',
                    border: '1px solid black',
                    padding: '10px',
                    zIndex: 1000
                  }}
              >
                <ul>
                  <li style={this.menuItemStyle} onClick={() => {
                    this.props.moveElementLayerToTop(this.state.selectedShape!);
                    this.setState({popupMenuVisible: false});
                  }}>
                    Bring Forward
                  </li>
                  <li style={this.menuItemStyle} onClick={() => {
                    this.props.moveElementLayerToBottom(this.state.selectedShape!);
                    this.setState({popupMenuVisible: false});
                  }}>
                    Send Backward
                  </li>
                </ul>
              </div>
          )}
        </Card>
    )
  }
}

function mapStateToProps (state: RootState) {
  const { labelBuilder } = produce(state, draft => draft)
  const { stage, elements, selectedElements, zoom } = labelBuilder.markup
  // GS1 barcodes are sized by module width in their panel, never stretched.
  const gs1Selected = elements.some(element => element.type === 'gs1_barcode' && (selectedElements || []).includes(element.id))
  return { stage, elements, zoom, selectedElements, gs1Selected }
}

const connector = connect(mapStateToProps, {
  resetSelectedElement,
  transformElement,
  dragElement,
  replaceSelectedElement,
  moveSelectedElement,
  moveElementLayerToBottom,
  moveElementLayerToTop,
  replaceElementText,
  clearSelectedElement
})

export default connector(Template)
